import React, { Fragment } from 'react'
import { Accordion, AccordionSummary, AccordionDetails, Grid, Typography, Button, Box, TextField, FormControlLabel, Checkbox, Divider } from '@material-ui/core'
import { Add, Save, Delete, KeyboardArrowDown } from '@material-ui/icons'
import { useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import i18n from 'i18next'
import moment from 'moment'
import { useChangeHandler, useUpdate, useRemove, useAlert } from '../reducer'
import { useFetch, mapByIds, useAuth } from '../context/useFetch'
import { MonthPicker } from '../utils/MonthPicker'

const initialBackground = {
  from: '',
  to: '',
  role: '',
  structure: '',
  location: '',
  background_type: 'education'
}

const sectionInfos = {
  education: {
    type: 'education',
    label: i18n.t('profile.background.education.label'),
    roleLabel: i18n.t('profile.background.education.roleLabel'),
    today: i18n.t('profile.background.education.today'),
    currentDuration: i18n.t('profile.background.education.currentDuration'),
    oldDuration: i18n.t('profile.background.education.oldDuration'),
    blankSlate: i18n.t('profile.background.education.blankSlate'),
    add: i18n.t('profile.background.education.add'),
    save: i18n.t('profile.background.education.save'),
    delete: i18n.t('profile.background.education.delete')
  },
  experience: {
    type: 'experience',
    label: i18n.t('profile.background.experience.label'),
    roleLabel: i18n.t('profile.background.experience.roleLabel'),
    today: i18n.t('profile.background.experience.today'),
    currentDuration: i18n.t('profile.background.experience.currentDuration'),
    oldDuration: i18n.t('profile.background.experience.oldDuration'),
    blankSlate: i18n.t('profile.background.experience.blankSlate'),
    add: i18n.t('profile.background.experience.add'),
    save: i18n.t('profile.background.experience.save'),
    delete: i18n.t('profile.background.experience.delete')
  }
}

export default () => (
  <Fragment>
    <Typography variant="h2" component="h1">
      {i18n.t('profile.background.page-title')}
    </Typography>
    <BackgroundSections />
  </Fragment>
)

const BackgroundSections = () =>
  useFetch(
    ({ fetch }) => ({
      backgrounds: fetch('profile').then(({ backgrounds }) => mapByIds(backgrounds))
    }),
    () => {
      const auth = useAuth()

      const update = useUpdate()
      const alert = useAlert()

      const backgrounds = useSelector((state) => state.backgrounds)

      const invalidDates = () => Object.values(backgrounds).some((background) => moment(background.from).isAfter(moment(background.to)))

      const submitHandler = (event) => {
        event.preventDefault()

        if (invalidDates()) {
          alert('error', i18n.t('profile.background.alerts.invalid_date'))
          return
        }

        auth
          .fetch('profile', {
            method: 'PATCH',
            body: {
              backgrounds_attributes: Object.values(backgrounds).map(({ isNew, ongoing, to, id, ...other }) => ({
                ...other,
                id: isNew ? null : id,
                to: ongoing ? null : to
              })
              )
            }
          })
          .then(() => {
            auth.fetch('profile').then(({ backgrounds }) => update(['backgrounds'], mapByIds(backgrounds)))

            alert('notice', i18n.t('profile.background.alerts.notice'))
          })
          .catch(() => {
            alert('error', i18n.t('profile.background.alerts.error'))
          })
      }
      return (
        <Fragment>
          <form onSubmit={submitHandler}>
            {Object.entries(sectionInfos).map(([key, _]) => (
              <Section type={key} />
            ))}
          </form>
        </Fragment>
      )
    }
  )

const Section = ({ type }) => {
  const sectionBackgrounds = useSelector((state) => Object.values(state.backgrounds).filter(({ background_type }) => background_type === type))

  const update = useUpdate()
  const addBackground = (type) => () => {
    const uuid = uuidv4()
    update(['backgrounds', uuid], {
      ...initialBackground,
      background_type: type,
      id: uuid,
      isNew: true
    })
    update(['currentBackgroundId'], uuid)

    setTimeout(() => {
      location.href = `#background-${uuid}`
    }, 100)
  }

  return (
    <Grid className="profileSection">
      <Typography variant="h3" component="h2">
        {sectionInfos[type].label}
      </Typography>

      {sectionBackgrounds.map(({ id }) => (
        <Background id={id} />
      ))}

      {sectionBackgrounds.length > 0 && (
        <Box align="right" mt={2}>
          <Button endIcon={<Add />} color="primary" onClick={addBackground(type)}>
            {sectionInfos[type].add}
          </Button>
        </Box>
      )}

      {sectionBackgrounds.length === 0 && (
        <Fragment>
          <Box component="p" mt={0} mb={2} color="text.secondary">
            {sectionInfos[type].blankSlate}
          </Box>
          <Divider />
          <Box align="right" mt={2}>
            <Button endIcon={<Add />} color="primary" onClick={addBackground(type)}>
              {sectionInfos[type].add}
            </Button>
          </Box>
        </Fragment>
      )}
    </Grid>
  )
}

const Background = ({ id }) => {
  const remove = useRemove()
  const update = useUpdate()

  const background = useSelector((state) => state.backgrounds[id])
  const currentBackgroundId = useSelector(({ currentBackgroundId }) => currentBackgroundId)

  const handler = useChangeHandler(background, 'backgrounds', id)
  const removeHandler = () => {
    background.isNew ? remove(['backgrounds', id]) : update(['backgrounds', id, '_destroy'], true)
  }

  const handleCheckbox = ({ target: { checked } }) => {
    update(['backgrounds', id, 'to'], checked ? null : moment().format('YYYY-MM-DD'))
  }

  const humanizedDuration = () => {
    if (!background.from) return ''
    return moment.duration(moment(background.from).diff(moment(background.to || moment()))).humanize()
  }

  const addDescriptionHandler = () => {
    update(['backgrounds', id, 'addDescription'], true)
  }

  const handleChange = (_event, expanded) => {
    update(['currentBackgroundId'], expanded ? id : null)
  }

  return (
    <Accordion
      className="profileEdition"
      style={{ opacity: background._destroy ? 0.3 : 1 }}
      expanded={currentBackgroundId === background.id} onChange={handleChange}
    >
      <AccordionSummary
        id={'panel-header-' + background.id}
        expandIcon={<KeyboardArrowDown />}
        aria-controls={'panel-content-' + background.id}
        className="profileEdition"
      >
        <div className="summaryHeader">
          <Typography component="h3" variant="h4">
            {background.structure ? background.structure : i18n.t('profile.background.labels.structure')}
          </Typography>
          <Typography component="span" color="textSecondary">
            {background.role ? background.role : sectionInfos[background.background_type].roleLabel}
          </Typography>
        </div>
        <Typography component="date" color="textSecondary">
          {moment(background.from ? background.from : undefined).format('YYYY')}
          {' - '}
          {moment(background.to ? background.to : undefined).format('YYYY')}
        </Typography>
      </AccordionSummary>

      <AccordionDetails
        className="profileEdition"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              id="background-structure"
              fullWidth
              label={i18n.t('profile.background.labels.structure')}
              required
              variant="filled"
              InputProps={{ disableUnderline: true }}
              {...handler.default('structure')}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              id="background-role"
              variant="filled"
              label={sectionInfos[background.background_type].roleLabel}
              fullWidth
              required
              InputProps={{ disableUnderline: true }}
              {...handler.default('role')}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              id="background-location"
              variant="filled"
              label={i18n.t('profile.background.labels.location')}
              fullWidth
              InputProps={{ disableUnderline: true }}
              {...handler.default('location')}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              label={sectionInfos[background.background_type].today}
              value
              checked={!background.to}
              onChange={handleCheckbox}
              control={<Checkbox color="primary" />}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <MonthPicker
              id="portfolio-date-start"
              label={i18n.t('profile.background.labels.de')}
              {...handler.default('from')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <MonthPicker
              id="portfolio-date-end"
              label={i18n.t('profile.background.labels.a')}
              disabled={!background.to}
              {...handler.default('to')}
            />

            <Typography variant="caption" component="p" style={{ paddingTop: '8px' }}>
              {(background.to) && (
                sectionInfos[background.background_type].oldDuration + ' ' + humanizedDuration() + '.'
              )}
              {(!background.to) && (
                sectionInfos[background.background_type].currentDuration + ' ' + humanizedDuration() + '.'
              )}
            </Typography>
          </Grid>

          {(background.background_type === 'experience' ||
            Boolean(background.description) ||
            background.addDescription) && (
              <Grid item xs={12}>
                <TextField
                  id="background-description"
                  variant="filled"
                  label={i18n.t('profile.background.labels.description')}
                  fullWidth
                  multiline
                  rows={3}
                  rowsMax={9}
                  InputProps={{ disableUnderline: true }}
                  {...handler.default('description')}
                />
              </Grid>
            )}
          {background.background_type === 'education' &&
            !Boolean(background.description) &&
            !background.addDescription && (
              <Grid item xs={12}>
                <Button type="text" onClick={addDescriptionHandler}>
                  {i18n.t('profile.background.labels.add_description')}
                </Button>
              </Grid>
            )}

          <Grid item xs={12} my={3}>
            <Divider />
          </Grid>

          <Grid item xs={12} container justifyContent="space-between">
            {!background._destroy && (
              <Button type="submit" variant="outlined" size="small" startIcon={<Delete />} onClick={removeHandler}>
                {sectionInfos[background.background_type].delete}
              </Button>
            )}

            <Button type="submit" variant="contained" size="small" startIcon={<Save />} color="primary">
              {sectionInfos[background.background_type].save}
            </Button>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}